<template>
  <div class="pptpreview">
    <div class="topbar">
      <el-button icon="el-icon-back" @click="$router.push('/pptlist')">返回列表</el-button>
      <el-input v-model="title" readonly style="width: 320px; margin-left: 12px"></el-input>
      <el-button type="success" style="margin-left: 12px" icon="el-icon-download" :loading="exportingProtected" @click="handleExportProtected">导出受保护版</el-button>
      <el-button icon="el-icon-view" :loading="exportingVerify" @click="handleExportVerify">导出验证版</el-button>
      <span style="margin-left: 14px; font-size: 12px; color: #999">「受保护版」上传 eOnline 用；「验证版」本地双击验证用，勿上传。整页渲染交付（保真 = PowerPoint 原样），底部按钮或 ← → 键翻页逐页检查</span>
    </div>
    <div class="stage">
      <div v-if="pages.length" ref="stage" class="stage-inner"></div>
      <div v-else class="empty">{{ loading ? '加载中…' : '无页面数据（可能全部页已删除），请返回重新导入转换' }}</div>
      <!-- 可视化翻页控件（键盘 ←/→/空格 亦可翻页） -->
      <div v-if="navCtl && pages.length" class="stage-nav">
        <button class="nav-btn" @click="navPrev"><i class="el-icon-arrow-left"></i></button>
        <span class="nav-info">{{ navIndex + 1 }} / {{ pages.length }}</span>
        <button class="nav-btn" @click="navNext"><i class="el-icon-arrow-right"></i></button>
      </div>
    </div>
    <!-- 导出设置（水印文字/位置模式），确认后按所选类型导出 -->
    <export-wm-dialog ref="wmDialog" v-model="wmDialogVisible" :preset-text="wmPreset" @confirm="onWmConfirm" />
  </div>
</template>

<script>
import { getPptProjectById } from '@/api/ppt'
import { renderRasterInto } from '@/utils/pptRender'
import { exportProtectedHtml, exportVerifyHtml } from '@/utils/pptExport'
import ExportWmDialog from '../components/export-wm-dialog.vue'

export default {
  name: 'pptpreview',
  components: { ExportWmDialog },
  data() {
    return {
      id: null,
      title: '',
      pages: [],
      pageW: 960,
      pageH: 540,
      loading: false,
      exportingProtected: false,
      exportingVerify: false,
      navCtl: null,
      navIndex: 0,
      // 导出设置弹窗：wmKind=protected/verify
      wmDialogVisible: false,
      wmPreset: '',
      wmKind: 'protected'
    }
  },
  mounted() {
    this.id = this.$route.query.id
    if (this.id) this.load()
  },
  methods: {
    load() {
      this.loading = true
      getPptProjectById(this.id)
        .then((res) => {
          if (res.data.code !== 0 || !res.data.data) {
            this.$message.error((res.data && res.data.message) || '获取项目失败')
            return
          }
          const p = res.data.data
          this.title = p.title || ''
          let pages = []
          try {
            pages = JSON.parse(p.pagesJson || '[]')
          } catch (e) {}
          // 画布尺寸（pt），videoBox 定位与导出视频页需要
          try {
            const theme = JSON.parse(p.themeJson || '{}')
            if (theme.pageW) this.pageW = Number(theme.pageW)
            if (theme.pageH) this.pageH = Number(theme.pageH)
          } catch (e) {}
          this.pages = pages
          this.$nextTick(() => {
            if (this.$refs.stage && pages.length) {
              this.navCtl = renderRasterInto(this.$refs.stage, pages, {
                showInfo: true,
                pageW: this.pageW,
                pageH: this.pageH,
                onChange: (idx) => { this.navIndex = idx }
              })
            }
          })
        })
        .catch((err) => this.$message.error('加载失败：' + err))
        .finally(() => {
          this.loading = false
        })
    },
    navPrev() {
      if (this.navCtl) this.navCtl.prev()
    },
    navNext() {
      if (this.navCtl) this.navCtl.next()
    },
    // 受保护版导出：弹导出设置（可加水印）→ 导出
    handleExportProtected() {
      if (!this.pages.length || !this.pages[0].img) {
        this.$message.warning('该项目没有可导出的渲染页面（可能仍是旧的元素结构）。')
        return
      }
      this.wmKind = 'protected'
      this.wmPreset = ''
      this.openWmDialog()
    },
    handleExportVerify() {
      if (!this.pages.length || !this.pages[0].img) {
        this.$message.warning('该项目没有可导出的渲染页面（可能仍是旧的元素结构）。')
        return
      }
      this.wmKind = 'verify'
      this.wmPreset = '验证专用·禁止上传'
      this.openWmDialog()
    },
    // 打开导出设置弹窗；已开着（连点两个导出按钮）时手动重初始化表单，套用新类型默认值
    openWmDialog() {
      this.wmDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.wmDialog) this.$refs.wmDialog.initForm()
      })
    },
    // 导出设置确认：wm = { text, mode, corners, density }
    async onWmConfirm(wm) {
      if (this.wmKind === 'verify') {
        this.exportingVerify = true
        try {
          await exportVerifyHtml(
            { title: this.title, pages: this.pages, pageW: this.pageW, pageH: this.pageH },
            { watermark: wm }
          )
          this.$message.success('已导出验证版，可本地双击检查（勿上传 eOnline）')
        } catch (e) {
          this.$message.error('导出失败：' + ((e && e.message) || e))
        } finally {
          this.exportingVerify = false
        }
      } else {
        this.exportingProtected = true
        try {
          await exportProtectedHtml(
            { title: this.title, pages: this.pages, pageW: this.pageW, pageH: this.pageH },
            { watermark: wm, onProgress: () => { this.exportingProtected = true } }
          )
          this.$message.success('已导出受保护版，请上传 eOnline（本地打不开属正常）')
        } catch (e) {
          this.$message.error('导出失败：' + ((e && e.message) || e))
        } finally {
          this.exportingProtected = false
        }
      }
    }
  }
}
</script>

<style scoped>
/* 高度预算：外层 home 布局占高 = el-header(3.1vw) + el-main 默认内边距(20px×2) + 备案footer(约64px)
   全部扣除，页面恰好落在外壳可视区内、不产生滚动，舞台不会被顶出屏幕 */
.pptpreview { height: calc(100vh - 3.1vw - 104px); display: flex; flex-direction: column; background: #141824; }
.topbar {
  display: flex; align-items: center; padding: 10px 16px;
  background: #fff; border-bottom: 1px solid #e6e6e6;
}
.stage { flex: 1; position: relative; overflow: hidden; }
.stage-inner { position: absolute; inset: 0; }
/* slide 填满舞台并显式居中（不依赖浏览器对"绝对定位静态位置"的解析差异），图片 contain 最大化 */
.stage-inner /deep/ .slide { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; }
.stage-inner /deep/ .slide.active { display: flex; }
.stage-inner /deep/ .slide img { display: block; max-width: 100%; max-height: 100%; box-shadow: 0 12px 40px rgba(0,0,0,.5); background: #fff; }
/* 底部翻页控件：与导出 HTML 的导航一致（‹ x/n ›） */
.stage-nav { position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%); display: flex; align-items: center; gap: 14px; z-index: 10; }
.nav-btn { width: 42px; height: 42px; border-radius: 50%; border: none; cursor: pointer; background: rgba(255,255,255,.16); color: #fff; font-size: 18px; display: flex; align-items: center; justify-content: center; }
.nav-btn:hover { background: rgba(255,255,255,.3); }
.nav-info { color: rgba(255,255,255,.85); font-size: 14px; min-width: 56px; text-align: center; }
.empty { color: #999; margin: 40px; text-align: center; }
</style>
